import React, { useState, useEffect } from 'react';
import { Plus, Search, Clock, ChevronDown, Pencil, Trash2, GripVertical } from 'lucide-react';
import EmptyState from '../../components/EmptyState';
import FlatRateModal from '../../components/FlatRateModal';
import { getFlatRateItems, getFlatRateCategories, deleteFlatRateItem, FlatRateItem, FlatRateCategory } from '../../lib/flat-rates';
import { useBusiness } from '../../contexts/BusinessContext';

export default function FlatRates() {
  const { selectedBusiness } = useBusiness();
  const [items, setItems] = useState<FlatRateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FlatRateItem | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [categories, setCategories] = useState<FlatRateCategory[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const [itemsData, categoriesData] = await Promise.all([
        getFlatRateItems(selectedBusiness.id),
        getFlatRateCategories(selectedBusiness.id)
      ]);
      setItems(itemsData);
      setCategories(categoriesData);
      setError(null);
    } catch (err) {
      console.error('Error loading flat rate items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load flat rate items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [selectedBusiness?.id]);

  const handleAddItem = () => {
    setSelectedItem(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditItem = (item: FlatRateItem) => {
    setSelectedItem(item);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      setLoading(true);
      await deleteFlatRateItem(id);
      await loadItems();
    } catch (err) {
      console.error('Error deleting flat rate item:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete flat rate item');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const filteredItems = items.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower)
    );
  });

  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    const grouped = items.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: FlatRateItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  return (
    <div className="space-y-6 pb-24">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Flat Rate Book</h1>
          <p className="mt-1 text-sm text-gray-500">Manage flat rate items and their hours</p>
        </div>
        <button 
          onClick={handleAddItem}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 transition-colors"
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Item
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-[#e1e3e5]">
        <div className="px-4 py-3 border-b border-[#e1e3e5] flex justify-between items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-9 pr-4 py-1.5 text-xs bg-gray-50 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
              <p className="mt-4 text-sm text-gray-600">Loading items...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <EmptyState
              icon={<Clock className="w-8 h-8 text-gray-400" />}
              title="No flat rate items"
              description="Start adding flat rate items to track resource hours."
              actionLabel="Add your first item"
              onAction={handleAddItem}
            />
          </div>
        ) : (
          <div className="space-y-6">
            {itemsByCategory.map((category) => (
              <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div 
                  className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between cursor-pointer"
                  onClick={() => toggleCategory(category.id)}
                >
                  <div className="flex items-center space-x-2">
                    <ChevronDown 
                      className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                        expandedCategories.has(category.id) ? 'rotate-180' : ''
                      }`}
                    />
                    <h3 className="text-sm font-medium text-gray-900">{category.name}</h3>
                    <span className="text-xs text-gray-500">({category.items.length})</span>
                  </div>
                </div>
                {expandedCategories.has(category.id) && (
                  <div className="divide-y divide-gray-200">
                    {category.items.map((item) => (
                      <div key={item.id} className="p-4 hover:bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-3">
                            <button className="cursor-grab active:cursor-grabbing p-1 -ml-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100">
                              <GripVertical className="h-4 w-4" />
                            </button>
                            <div>
                              <h3 className="text-sm font-medium text-gray-900">{item.name}</h3>
                              <div className="mt-2 flex items-center text-xs text-gray-500">
                                <Clock className="h-3.5 w-3.5 mr-1" />
                                {item.hours} hours
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEditItem(item)}
                              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="p-1.5 text-red-400 hover:text-red-600 rounded-md hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <FlatRateModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedItem(null);
          }}
          onSuccess={() => {
            setIsModalOpen(false);
            setSelectedItem(null);
            loadItems();
          }}
          businessId={selectedBusiness?.id}
          item={selectedItem}
          mode={modalMode}
        />
      </div>
    </div>
  );
}